import { DateTimeFormats } from '@src/app/enums';
import { useAppSelector } from '@src/app/hooks/store';
import { PerformanceChartTable } from '@src/app/store/reducers/performance/types';
import { selectPortfolios } from '@src/app/store/reducers/portfolios/selectors';
import strings from '@src/app/strings/LocalisedStrings';
import { ChartSchema } from '@src/app/types';
import { dateFormatter } from '@src/app/utils/GenericUtils';
import { MultiPortfolioPerformanceDetails } from 'clientportalshared';
import FusionCharts from 'fusioncharts';
import { FC, useMemo } from 'react';
import ReactFC from 'react-fusioncharts';

interface PerformanceChartProps {
  performance: MultiPortfolioPerformanceDetails;
}

const PerformanceChart: FC<PerformanceChartProps> = ({ performance }: PerformanceChartProps): JSX.Element => {
  const { portfoliosCombined } = useAppSelector(selectPortfolios);
  const dailyPerformance = performance?.dailyPerformance ?? [];
  const dailyBenchmark = performance?.dailyBenchmark ?? [];

  const chartSchema = useMemo(() => {
    const schema: ChartSchema[] = [{ name: strings.time, type: 'date', format: '%d-%m-%Y' }];

    dailyPerformance.forEach((node, index) => {
      if (!portfoliosCombined && node.portfolioId === 0 && node.portfolioName === 'Combined') return;

      const portfolioId = node.portfolioId ?? index;
      const portfolioName = node.portfolioName ?? strings.portfolio;
      schema.push({
        name: portfolioId === 0 ? portfolioName : `${portfolioName} (${portfolioId})`,
        type: 'number',
      });
    });

    if (dailyBenchmark.length > 0) {
      schema.push({
        name: strings.benchmark,
        type: 'number',
      });
    }
    return schema;
  }, [performance, portfoliosCombined]);

  const table: PerformanceChartTable = useMemo(() => {
    const tempDataMap = new Map<string, (string | number)[]>();
    const hasDailyBenchmark = dailyBenchmark.length > 0;
    const plotNames: string[] = [];
    const includedPerformances = dailyPerformance.filter(
      (item) => portfoliosCombined || !(item.portfolioId === 0 && item.portfolioName === 'Combined')
    );

    const getOrCreateRow = (date: string) => {
      if (!tempDataMap.has(date)) {
        const rowLength = includedPerformances.length + (hasDailyBenchmark ? 2 : 1);
        const newRow = new Array(rowLength).fill(null);
        newRow[0] = date;
        tempDataMap.set(date, newRow);
      }
      return tempDataMap.get(date)!;
    };

    dailyPerformance.forEach((item, index) => {
      if (!portfoliosCombined && item.portfolioId === 0 && item.portfolioName === 'Combined') return;

      const performanceChartSeriesData = item.performanceChartSeriesData;
      if (!performanceChartSeriesData) return;

      performanceChartSeriesData.forEach((series) => {
        const date = dateFormatter(series.date, false, DateTimeFormats.FcDateFormat);
        const row = getOrCreateRow(date);
        row[index + 1] = series.cumulativeReturn * 100;
      });

      const portfolioId = item.portfolioId ?? index;
      const portfolioName = item.portfolioName ?? strings.portfolio;
      plotNames.push(portfolioId === 0 ? portfolioName : `${portfolioName} (${portfolioId})`);
    });

    if (hasDailyBenchmark) {
      dailyBenchmark.forEach((item) => {
        const date = dateFormatter(item.date, false, DateTimeFormats.FcDateFormat);
        const row = getOrCreateRow(date);
        row[includedPerformances.length + 1] = item.cumulativeReturn * 100;
      });
      plotNames.push('Benchmark');
    }

    return { data: Array.from(tempDataMap.values()), plotNames: plotNames };
  }, [dailyPerformance, dailyBenchmark, portfoliosCombined]);

  const fusionDataTable = new FusionCharts.DataStore().createDataTable(table.data, chartSchema);

  const chartConfig = {
    type: 'timeseries',
    width: '100%',
    height: '400',
    dataFormat: 'json',
    dataEmptyMessage: strings.emptyPortfolioDataMessage,
    dataSource: {
      chart: {
        theme: 'fusion',
        lineThickness: '2',
        showLegend: 0,
        showTimeNav: 0,
      },
      tooltip: {
        style: {
          container: {
            minWidth: '180px',
            maxWidth: '300px',
          },
        },
      },
      navigator: {
        enabled: 0,
      },
      yaxis: [
        {
          plot: table.plotNames.map((name) => ({
            value: name,
            type: 'line',
            title: name,
            connectnulldata: true,
          })),
          title: '',
          format: {
            suffix: '%',
          },
        },
      ],
      extensions: {
        standardRangeSelector: {
          enabled: '0',
        },
        customRangeSelector: {
          enabled: '0',
        },
      },
      data: fusionDataTable,
    },
  };

  return <ReactFC {...chartConfig} />;
};

export default PerformanceChart;
